import { gatewayApi as api } from './gatewayApi';

// Types
export interface RouteCard {
  id?: string;
  Name: string;
  Description: string;
  Issue: string;
  Version: string;
  PartId: string;
  ClientId: string;
  RCTStatusId: number;
  EstimatedTime: number;
  Active: boolean;
  CreatedDate?: string;
  ModifiedDate?: string;

  // Related entities
  Part?: {
    id: string;
    Number: string;
    Description: string;
    Issue: string;
    Classification: string;
  };

  Client?: {
    id: string;
    Name: string;
    Code: string;
  };

  Status?: {
    id: number;
    StatusName: string;
    Color: string;
  };

  Operations?: RouteCardOperation[];
  Documents?: RouteCardDocument[];
}

export interface RouteCardOperation {
  id?: string;
  RCTId: string;
  OperationId: string;
  Sequence: number;
  EstimatedTime: number;
  SetupTime: number;
  TeardownTime: number;
  Active: boolean;

  // Related entities
  Operation?: {
    id: string;
    Name: string;
    Description: string;
    OperationType?: {
      id: string;
      Name: string;
    };
  };

  Inputs?: RouteCardOperationInput[];
  WorkInstructions?: WorkInstruction[];
  ToolFixtures?: ToolFixture[];
}

export interface RouteCardOperationInput {
  id?: string;
  RCTDetailId: string;
  InputId: string;
  Required: boolean;
  Active: boolean;

  // Related entities
  Input?: {
    id: string;
    Name: string;
    InputType?: {
      id: string;
      Name: string;
    };
  };
}

export interface WorkInstruction {
  id?: string;
  Name: string;
  Description: string;
  FilePath?: string;
  Active?: boolean;
}

export interface ToolFixture {
  id?: string;
  Name: string;
  SerialNumber: string;
  ToolTypeId: string;
  Active?: boolean;

  // Related entities
  ToolType?: {
    id: string;
    Name: string;
  };
}

export interface RouteCardDocument {
  id: string;
  Name: string;
  DocumentNum: string;
  Description: string;
  Version: string;
  FilePath?: string;
}

export interface Operation {
  id: string;
  Name: string;
  Description: string;
  OperationTypeId: string;
  EstimatedTime: number;
  Active: boolean;

  OperationType?: {
    id: string;
    Name: string;
  };
}

export interface Client {
  id: string;
  Name: string;
  Code: string;
  Active: boolean;
}

export interface Part {
  id: string;
  Number: string;
  Description: string;
  Issue: string;
  Classification: string;
  ClientId: string;
  Active: boolean;
}

export interface RouteCardStatus {
  id: number;
  StatusName: string;
  Color: string;
  Active: boolean;
}

export interface CreateRouteCardRequest {
  Name: string;
  Description: string;
  Issue: string;
  Version: string;
  PartId: string;
  ClientId: string;
  operations?: Omit<RouteCardOperation, 'id' | 'RCTId'>[];
}

export interface SearchRouteCardsRequest {
  pageNum?: number;
  pageSize?: number;
  sort?: string;
  filter?: {
    ClientId?: string;
    PartId?: string;
    RCTStatusId?: number;
    Active?: boolean;
    [key: string]: any;
  };
}

export interface SearchRouteCardsResponse {
  data: RouteCard[];
  totalCount: number;
  pageNum: number;
  pageSize: number;
}

// Transform frontend data to backend format
const transformToBackendFormat = (rc: RouteCard) => ({
  Name: rc.Name,
  Description: rc.Description,
  PartId: parseInt(rc.PartId),
  ClientId: parseInt(rc.ClientId),
  saveOperations: rc.Operations?.map(op => ({
    OperationId: parseInt(op.OperationId),
    Sequence: op.Sequence,
    EstimatedTime: op.EstimatedTime,
    SetupTime: op.SetupTime || 0,
    TeardownTime: op.TeardownTime || 0,
    Active: op.Active
  })) || []
});

// Transform backend data to frontend format
const transformFromBackendFormat = (backendRC: any): RouteCard => ({
  id: backendRC.Id?.toString(),
  Name: backendRC.Name,
  Description: backendRC.Description,
  Issue: backendRC.Issue?.toString(),
  Version: backendRC.Version?.toString(),
  PartId: backendRC.PartId?.toString(),
  ClientId: backendRC.ClientId?.toString(),
  RCTStatusId: backendRC.RCTStatusId,
  EstimatedTime: backendRC.EstimatedTime || 0,
  Active: backendRC.Active,
  CreatedDate: backendRC.CreatedDate,
  ModifiedDate: backendRC.ModifiedDate,
  Part: backendRC.Part ? {
    id: backendRC.Part.Id?.toString(),
    Number: backendRC.Part.Number,
    Description: backendRC.Part.Description,
    Issue: backendRC.Part.Issue?.toString(),
    Classification: backendRC.Part.Classification
  } : undefined,
  Client: backendRC.Client ? {
    id: backendRC.Client.Id?.toString(),
    Name: backendRC.Client.Name,
    Code: backendRC.Client.Code
  } : undefined,
  Status: backendRC.Status ? {
    id: backendRC.Status.Id,
    StatusName: backendRC.Status.StatusName,
    Color: backendRC.Status.Color
  } : undefined,
  Operations: backendRC.Operations?.map((op: any) => ({
    id: op.Id?.toString(),
    RCTId: backendRC.Id?.toString(),
    OperationId: op.Id?.toString(),
    Sequence: op.Sequence || 0,
    EstimatedTime: op.EstimatedTime || 0,
    SetupTime: op.SetupTime || 0,
    TeardownTime: op.TeardownTime || 0,
    Active: op.Active,
    Operation: op.Operation ? {
      id: op.Operation.Id?.toString(),
      Name: op.Operation.Name,
      Description: op.Operation.Description,
      OperationType: op.Operation.OperationType ? {
        id: op.Operation.OperationType.Id?.toString(),
        Name: op.Operation.OperationType.Name
      } : undefined
    } : undefined,
    Inputs: op.Inputs || [],
    WorkInstructions: op.WorkInstructions || [],
    ToolFixtures: op.ToolFixtures || []
  })) || [],
  Documents: backendRC.Documents || []
});

// Route Card API Service
export const routeCardApi = {
  // Get all route cards with optional filtering
  getAll: (params?: SearchRouteCardsRequest): Promise<SearchRouteCardsResponse> =>
    api.post<SearchRouteCardsResponse>('/routecard/search', params).then(response => ({
      data: response.data?.map(transformFromBackendFormat) || [],
      totalCount: response.totalCount || 0,
      pageNum: response.pageNum || 1,
      pageSize: response.pageSize || 20
    })),

  // Get route card by ID
  getById: (id: string): Promise<RouteCard> =>
    api.get<{ data: any }>(`/routecard/${id}`).then(response => transformFromBackendFormat(response.data)),

  // Create new route card
  create: (data: CreateRouteCardRequest): Promise<RouteCard> =>
    api.post<{ data: any }>('/routecard/', { data: transformToBackendFormat(data as RouteCard) }).then(response => transformFromBackendFormat(response.data)),

  // Update route card
  update: (data: Partial<RouteCard>): Promise<RouteCard> =>
    api.put<{ data: any }>('/routecard/', { 
      id: parseInt(data.id!), 
      data: transformToBackendFormat(data as RouteCard) 
    }).then(response => transformFromBackendFormat(response.data)),

  // Delete route card
  delete: (id: string): Promise<void> =>
    api.delete<void>(`/routecard/${id}`),

  // Search route cards
  search: (field: string, value: string): Promise<RouteCard[]> =>
    api.get(`/routecard/search/${field}/${value}`),

  // Route Card Operations Management
  getOperations: (routeCardId: string): Promise<RouteCardOperation[]> =>
    api.get(`/routecarddetail/search`, { filter: { RCTId: routeCardId } }),

  addOperation: (routeCardId: string, operation: Omit<RouteCardOperation, 'id' | 'RCTId'>): Promise<RouteCardOperation> =>
    api.post('/routecarddetail/', { ...operation, RCTId: routeCardId }),

  updateOperation: (operationData: RouteCardOperation): Promise<RouteCardOperation> =>
    api.put('/routecarddetail/', operationData),

  deleteOperation: (operationId: string): Promise<void> =>
    api.delete(`/routecarddetail/${operationId}`),

  reorderOperations: (routeCardId: string, operations: { id: string; sequence: number }[]): Promise<void> =>
    api.put('/routecarddetail/reorder', { routeCardId, operations }),

  // Operation Inputs Management
  getOperationInputs: (operationId: string): Promise<RouteCardOperationInput[]> =>
    api.get(`/wooperationinput/search`, { filter: { RCTDetailId: operationId } }),

  addOperationInput: (operationId: string, input: Omit<RouteCardOperationInput, 'id' | 'RCTDetailId'>): Promise<RouteCardOperationInput> =>
    api.post('/wooperationinput/', { ...input, RCTDetailId: operationId }),

  updateOperationInput: (inputData: RouteCardOperationInput): Promise<RouteCardOperationInput> =>
    api.put('/wooperationinput/', inputData),

  deleteOperationInput: (inputId: string): Promise<void> =>
    api.delete(`/wooperationinput/${inputId}`),

  // Work Instructions Management
  getWorkInstructions: (operationId: string): Promise<WorkInstruction[]> =>
    api.get(`/workinstruction/search`, { filter: { OperationId: operationId } }),

  addWorkInstruction: (operationId: string, instruction: Omit<WorkInstruction, 'id'>): Promise<WorkInstruction> =>
    api.post('/workinstruction/', { ...instruction, OperationId: operationId }),

  updateWorkInstruction: (instructionData: WorkInstruction): Promise<WorkInstruction> =>
    api.put('/workinstruction/', instructionData),

  deleteWorkInstruction: (instructionId: string): Promise<void> =>
    api.delete(`/workinstruction/${instructionId}`),

  // Tool Fixtures Management
  getToolFixtures: (operationId: string): Promise<ToolFixture[]> =>
    api.get(`/toolfixture/search`, { filter: { OperationId: operationId } }),

  addToolFixture: (operationId: string, toolFixture: Omit<ToolFixture, 'id'>): Promise<ToolFixture> =>
    api.post('/toolfixture/', { ...toolFixture, OperationId: operationId }),

  updateToolFixture: (toolFixtureData: ToolFixture): Promise<ToolFixture> =>
    api.put('/toolfixture/', toolFixtureData),

  deleteToolFixture: (toolFixtureId: string): Promise<void> =>
    api.delete(`/toolfixture/${toolFixtureId}`),

  // Route Card Documents
  getDocuments: (routeCardId: string): Promise<RouteCardDocument[]> =>
    api.get(`/routecard/${routeCardId}/documents`),

  uploadDocument: (routeCardId: string, formData: FormData): Promise<RouteCardDocument> =>
    api.upload(`/routecard/${routeCardId}/documents`, formData),

  deleteDocument: (routeCardId: string, documentId: string): Promise<void> =>
    api.delete(`/routecard/${routeCardId}/documents/${documentId}`),

  // Route Card Status Management
  approve: (id: string, comment?: string): Promise<RouteCard> =>
    api.put('/routecard/approve', { id, comment }),

  reject: (id: string, comment?: string): Promise<RouteCard> =>
    api.put('/routecard/reject', { id, comment }),

  pending: (id: string, comment?: string): Promise<RouteCard> =>
    api.put('/routecard/pending', { id, comment }),

  // Get Dropdown Data
  getClients: (): Promise<Client[]> =>
    api.get('/client', { page: 1, limit: 100 }),

  getParts: (clientId?: string): Promise<Part[]> =>
    clientId ? api.get('/part', { ClientId: clientId, page: 1, limit: 100 }) : api.get('/part', { page: 1, limit: 100 }),

  getOperationsList: (): Promise<Operation[]> =>
    api.get('/operation', { page: 1, limit: 100 }),

  getRouteCardStatuses: (): Promise<RouteCardStatus[]> =>
    api.get('/status', { page: 1, limit: 100 }),

  // Utility methods
  duplicate: (id: string, newName: string): Promise<RouteCard> =>
    api.post('/routecard/duplicate', { id, newName }),

  getByPart: (partId: string): Promise<RouteCard[]> =>
    api.get(`/routecard/search/PartId/${partId}`),

  getByClient: (clientId: string): Promise<RouteCard[]> =>
    api.get(`/routecard/search/ClientId/${clientId}`),

  exportToPdf: (id: string): Promise<Blob> =>
    api.getBlob(`/routecard/${id}/export`),

  importFromFile: (file: File): Promise<RouteCard> => {
    const formData = new FormData();
    formData.append('file', file);
    return api.upload('/routecard/import', formData);
  },
}; 